import { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Chip, FormControl, LinearProgress, MenuItem, Pagination, Paper, Select, SelectChangeEvent, Skeleton, Snackbar, SnackbarCloseReason, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, useTheme, alpha } from '@mui/material';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { paperListagemSx } from '../../../shared/components';
import { LayoutBaseDePagina } from '../../../shared/layouts';
import { useDebounce, useListagemAltura, useListagemKeyboardNav } from '../../../shared/hooks';
import { Environment } from '../../../shared/environment';
import { VendasService, IListagemMovimento } from '../../../shared/services/api/estoque/venda/venda';
import { brand } from '../../../shared/themes';
import dayjs from 'dayjs';
import { FerramentasDaListagemEntrada } from '../../../shared/components/ferramentas-da-listagem/FerramentasDaListagemEntrada';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCartRounded';

const OPCOES_POR_PAGINA = [10, 25, 50, 100];

export const ListagemDeVenda: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestIdRef = useRef(0);
  const { debounce } = useDebounce();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const [rows, setRows] = useState<IListagemMovimento[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [openMsg, setOpenMsg] = useState(false);
  const [msg, setMsg] = useState('');
  const [msgType, setMsgType] = useState<'warning' | 'error' | 'info' | 'success'>('warning');
  const ROWS_CACHE_KEY = 'vendas';
  // Altura real de uma linha: padding vertical (py: 1.25 => 20px) + line-height do texto
  // (variant body1 do tema, 1.6 * fontSize 13px = 20.8px) + borda (1px) ≈ 42px. Estava em 48
  // (superestimado) — a diferença, multiplicada pelas ~15-20 linhas que cabem na tela, é
  // exatamente o que sobrava como espaço vazio entre a última linha e o rodapé.
  const { tabelaRef, containerHeight, rowsPerPage, setRowsPerPage } = useListagemAltura({
    cacheKey: ROWS_CACHE_KEY,
    valorInicial: Environment.LIMITE_DE_LINHASSMALL,
    rowHeight: 42,
    footerReservado: 68,
    bottomGap: 24,
  });

  const handleChangeRowsPerPage = (event: SelectChangeEvent<number>) => {
    setRowsPerPage(Number(event.target.value));
  };

  const handleCloseMsg = (
    event?: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason,
  ) => {
    if (reason === 'clickaway') return;
    setOpenMsg(false);
  };

  const dataInicial = useMemo(() => {
    return searchParams.get('datainicial') || dayjs().startOf('month').format('DD/MM/YYYY').toString();
  }, [searchParams]);

  const dataFinal = useMemo(() => {
    return searchParams.get('datafinal') || dayjs().format('DD/MM/YYYY').toString();
  }, [searchParams]);
  const tipo = useMemo(() => {
    return searchParams.get('tipo') || 'data';
  }, [searchParams]);

  const filter = useMemo(() => {
    return searchParams.get('filter') || '';
  }, [searchParams]);

  const idparceiro = useMemo(() => {
    return searchParams.get('parceiro') || '';
  }, [searchParams]);

  const pagina = useMemo(() => {
    return Number(searchParams.get('pagina') || '1');
  }, [searchParams]);

  function verificaFormato(data: string) {
    const regex = /^\d{2}\/\d{2}\/\d{4}$/;
    return regex.test(data);
  }

  const [busca, setBusca] = useState(true);
  const [autoComplete, setAutoComplete] = useState(true);
  const [data, setData] = useState(true);

  useEffect(() => {
    if (tipo === 'data') {
      setData(true);
      setBusca(false);
      setAutoComplete(false);
      setRows([]);
      setSearchParams({ tipo: 'data', pagina: '1' }, { replace: true });
      setTotalCount(0);
    }
    else if (tipo === 'parceiro') {
      setAutoComplete(true);
      setBusca(false);
      setData(false);
      setRows([]);
      setSearchParams({ tipo: 'parceiro', pagina: '1' }, { replace: true });
      setTotalCount(0);
    }
    else if (tipo === 'operacao') {
      // Orçamento mostra data E parceiro juntos — o parceiro aqui é um filtro adicional
      // opcional, diferente do tipo "parceiro" onde ele é obrigatório para buscar. O valor
      // 'operacao' (não 'orcamento') é o que o controller do back-end espera em tipo — só
      // com esse valor exato ele aplica o filtro de operacao=... que mandamos.
      setAutoComplete(true);
      setBusca(false);
      setData(true);
      setRows([]);
      setSearchParams({ tipo: 'operacao', pagina: '1' }, { replace: true });
      setTotalCount(0);
    } else {
      setBusca(true);
      setAutoComplete(false);
      setData(false);
      setRows([]);
      setSearchParams({ tipo: 'numero', pagina: '1' }, { replace: true });
      setTotalCount(0);
    }
  }, [tipo]);

  useEffect(() => {
    if (tipo === 'numero' && filter != '') {
      const requestId = ++requestIdRef.current;
      setIsLoading(true);
      debounce(() => {
        if (requestId !== requestIdRef.current) return;
        VendasService.getAll(pagina, dataInicial, dataFinal, filter, tipo, rowsPerPage)
          .then((result) => {
            if (requestId !== requestIdRef.current) return;
            setIsLoading(false);
            if (result instanceof Error) {
              setMsgType('error');
              setMsg(result.message);
              setOpenMsg(true);
            } else {
              setTotalCount(result.totalCount);
              setRows(result.data);
            }
          });
      });
    }
  }, [filter, pagina, rowsPerPage]);

  useEffect(() => {
    // O "else" só deve limpar a listagem quando o tipo ativo É "data" mas o filtro de datas
    // está inválido — nunca quando o tipo ativo é outro (numero/parceiro). Antes o "else" era
    // avaliado sempre que tipo !== 'data' também, então toda vez que rowsPerPage/pagina mudava
    // (independente do tipo selecionado), este efeito zerava a listagem por cima do resultado
    // que o efeito do tipo realmente ativo estava buscando — causando o "pisca" de duas listagens.
    if (tipo !== 'data') return;

    if (verificaFormato(dataInicial) && verificaFormato(dataFinal)) {
      const requestId = ++requestIdRef.current;
      setIsLoading(true);
      debounce(() => {
        if (requestId !== requestIdRef.current) return;
        VendasService.getAll(pagina, dataInicial, dataFinal, filter, tipo, rowsPerPage)
          .then((result) => {
            if (requestId !== requestIdRef.current) return;
            setIsLoading(false);
            if (result instanceof Error) {
              setMsgType('error');
              setMsg(result.message);
              setOpenMsg(true);
            } else {
              setTotalCount(result.totalCount);
              setRows(result.data);
            }
          });
      });
    } else {
      setRows([]);
    }
  }, [dataInicial, dataFinal, pagina, tipo, rowsPerPage]);

  useEffect(() => {
    // Mesmo motivo do efeito acima: só limpa a listagem quando o tipo ativo É "parceiro"
    // mas o parceiro está vazio, nunca quando outro tipo está ativo.
    if (tipo !== 'parceiro') return;

    if (idparceiro != '') {
      const requestId = ++requestIdRef.current;
      setIsLoading(true);
      debounce(() => {
        if (requestId !== requestIdRef.current) return;
        VendasService.getAll(pagina, dataInicial, dataFinal, idparceiro, tipo, rowsPerPage)
          .then((result) => {
            if (requestId !== requestIdRef.current) return;
            setIsLoading(false);
            if (result instanceof Error) {
              setMsgType('error');
              setMsg(result.message);
              setOpenMsg(true);
            } else {
              setTotalCount(result.totalCount);
              setRows(result.data);
            }
          });
      });
    } else {
      setRows([]);
    }
  }, [idparceiro, pagina, rowsPerPage]);

  useEffect(() => {
    // Mesmo motivo dos efeitos acima: só limpa a listagem quando o tipo ativo É "operacao"
    // mas o filtro de datas está inválido. O parceiro aqui é opcional (diferente do tipo
    // "parceiro"), então não bloqueia a busca quando vazio.
    if (tipo !== 'operacao') return;

    if (verificaFormato(dataInicial) && verificaFormato(dataFinal)) {
      const requestId = ++requestIdRef.current;
      setIsLoading(true);
      debounce(() => {
        if (requestId !== requestIdRef.current) return;
        VendasService.getAll(pagina, dataInicial, dataFinal, idparceiro, tipo, rowsPerPage, Environment.URL_IDORCAMENTO)
          .then((result) => {
            if (requestId !== requestIdRef.current) return;
            setIsLoading(false);
            if (result instanceof Error) {
              setMsgType('error');
              setMsg(result.message);
              setOpenMsg(true);
            } else {
              setTotalCount(result.totalCount);
              setRows(result.data);
            }
          });
      });
    } else {
      setRows([]);
    }
  }, [dataInicial, dataFinal, idparceiro, pagina, tipo, rowsPerPage]);

  useEffect(() => {
    if (pagina !== 1) {
      setSearchParams({
        parceiro: idparceiro,
        filter: filter,
        tipo: tipo,
        datainicial: dataInicial,
        datafinal: dataFinal,
        pagina: '1'
      }, { replace: true });
    }
  }, [rowsPerPage]);

  const formatarDocumento = (documento: string) => {
    if (!documento) return '-';
    const apenasNumeros = documento.replace(/\D/g, '');
    if (apenasNumeros.length === 11) {
      return apenasNumeros.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    } else if (apenasNumeros.length === 14) {
      return apenasNumeros.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    }
    return documento;
  };

  const primeiroItem = totalCount === 0 ? 0 : (pagina - 1) * rowsPerPage + 1;
  const ultimoItem = Math.min(pagina * rowsPerPage, totalCount);
  const totalPaginas = Math.max(1, Math.ceil(totalCount / rowsPerPage));

  // Sem F8/refoco de busca aqui (omitindo tiposBusca/onTrocarTipo): esta tela alterna entre 4
  // widgets de busca bem diferentes entre si (datas, autocomplete de parceiro, texto de
  // número, data+parceiro de orçamento) em vez de um único campo de texto — não há um "input
  // de busca" único pra focar. A navegação entre linhas (setas/Enter/pular de página) continua
  // funcionando normalmente.
  const { linhaRefCallback, onLinhaKeyDown } = useListagemKeyboardNav({
    rowsLength: rows.length,
    pagina,
    totalPaginas,
    onIrParaPagina: (novaPagina) => setSearchParams({
      parceiro: idparceiro, filter, tipo, datainicial: dataInicial, datafinal: dataFinal, pagina: novaPagina.toString(),
    }, { replace: true }),
    onEnter: (index) => navigate(`/venda/${rows[index].idmov}`, { state: { from: `${location.pathname}${location.search}` } }),
    rowsDep: [rows],
  });

  return (
    <>
      <LayoutBaseDePagina
        titulo='Vendas'
        subtitulo='Visualize, pesquise e gerencie todas as vendas cadastradas no sistema.'
        barraDeFerramentas={
          <FerramentasDaListagemEntrada
            mostrarInputBusca={busca}
            mostrarInputBasicSelect
            mostrarInputData={data}
            mostrarAutoComplete={autoComplete}
            dataInicial={dataInicial}
            dataFinal={dataFinal}
            textoDaBusca={filter}
            textoDoSelect={tipo}
            textoBotaoNovo='Nova Venda'
            aoClicarEmNovo={() => navigate('/venda/novo', { state: { from: `${location.pathname}${location.search}` } })}
            aoMudarDataInicial={datainicial => setSearchParams({ tipo: tipo, pagina: '1', datainicial: datainicial, datafinal: dataFinal }, { replace: true })}
            aoMudarDataFinal={datafinal => setSearchParams({ tipo: tipo, pagina: '1', datainicial: dataInicial, datafinal: datafinal }, { replace: true })}
            aoMudarTextoDeBusca={texto => setSearchParams({ filter: texto, tipo: tipo, pagina: '1', datainicial: dataInicial, datafinal: dataFinal }, { replace: true })}
            aoMudarTextoDoSelect={textoselect => setSearchParams({ filter: filter, tipo: textoselect, pagina: '1', datainicial: dataInicial, datafinal: dataFinal }, { replace: true })}
            aoMudarTextoDoParceiro={(texto, texto1) => setSearchParams({ parceiroselect: texto1, parceiro: texto, filter: filter, tipo: tipo, pagina: '1', datainicial: dataInicial, datafinal: dataFinal }, { replace: true })}
            dadosSelecao={[
              { nameBD: 'data', descricao: 'Data' },
              { nameBD: 'parceiro', descricao: 'Cliente' },
              { nameBD: 'numero', descricao: 'Número' },
              { nameBD: 'operacao', descricao: 'Orçamento' },
            ]}
          />
        }
      >
        <Snackbar
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          open={openMsg}
          autoHideDuration={6000}
          onClose={handleCloseMsg}
        >
          <Alert
            onClose={handleCloseMsg}
            severity={msgType}
            variant='filled'
            sx={{ width: '100%' }}
          >
            {msg}
          </Alert>
        </Snackbar>
        <Paper
          elevation={0}
          sx={{
            ...paperListagemSx(theme),
            // Altura sempre fixa no espaço calculado (nunca 'auto' encolhendo pro tamanho do
            // conteúdo) — é isso que mantém a paginação colada na base da tela mesmo quando a
            // página atual tem poucas vendas, em vez de deixar uma área em branco entre o
            // rodapé e o limite da tela.
            height: containerHeight ? `${containerHeight}px` : 'calc(100vh - 220px)',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <TableContainer
            ref={tabelaRef}
            sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}
          >
            <Table size="small" sx={{ width: '100%', tableLayout: 'fixed' }}>
              {/* sticky — só os itens (tbody) rolam dentro do TableContainer; o cabeçalho
                  fica fixo no topo. As células já têm fundo opaco (torq.surface.page, ver
                  tema), então nada aparece "por baixo" ao rolar. */}
              <TableHead sx={{ position: 'sticky', top: 0, zIndex: 2 }}>
                <TableRow>
                  <TableCell sx={{ width: '110px' }}>Data</TableCell>
                  <TableCell sx={{ width: '90px' }}>Nº</TableCell>
                  <TableCell>Cliente</TableCell>
                  <TableCell sx={{ width: '140px' }}>Operação</TableCell>
                  <TableCell sx={{ width: '140px' }}>CPF/CNPJ</TableCell>
                  <TableCell align='right' sx={{ width: '120px' }}>Total</TableCell>
                </TableRow>
              </TableHead>

              {isLoading && rows.length === 0 && (
                <TableBody>
                  {Array.from({ length: Math.min(rowsPerPage, 10) }).map((_, index) => (
                    <TableRow key={index} sx={{ '& td': { py: 1.25, px: 1.5, borderBottom: `1px solid ${theme.torq.border.default}` } }}>
                      <TableCell><Skeleton variant="text" width="70%" /></TableCell>
                      <TableCell><Skeleton variant="rounded" width={50} height={22} /></TableCell>
                      <TableCell><Skeleton variant="text" width="80%" /></TableCell>
                      <TableCell><Skeleton variant="text" width="65%" /></TableCell>
                      <TableCell><Skeleton variant="text" width="75%" /></TableCell>
                      <TableCell align="right"><Skeleton variant="text" width="60%" sx={{ ml: 'auto' }} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              )}
              <TableBody>
                {rows.map((row, index) => (
                  <TableRow
                    key={row.idmov}
                    ref={linhaRefCallback(index)}
                    hover
                    tabIndex={0}
                    sx={{
                      cursor: 'pointer',
                      outline: 'none',
                      '&:last-child td': { borderBottom: 0 },
                      '& td': {
                        py: 1.25,
                        px: 1.5,
                        fontSize: '0.8125rem',
                        borderBottom: `1px solid ${theme.torq.border.default}`,
                      },
                      // Zebrado — linhas pares num tom sutil do menu lateral (brand.menuBase),
                      // ímpares na cor de fundo padrão da listagem.
                      '&:nth-of-type(even)': {
                        backgroundColor: alpha(brand.menuBase, theme.palette.mode === 'dark' ? 0.08 : 0.04),
                      },
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.035),
                      },
                      // Indicador visual de qual linha está focada — necessário porque a
                      // navegação por teclado (setas) move o foco entre as linhas.
                      '&:focus-visible': {
                        outline: `2px solid ${theme.torq.border.focus}`,
                        outlineOffset: '-2px',
                        backgroundColor: alpha(theme.palette.primary.main, 0.05),
                      },
                    }}
                    onClick={() => navigate(`/venda/${row.idmov}`, { state: { from: `${location.pathname}${location.search}` } })}
                    onKeyDown={onLinhaKeyDown(index)}
                  >
                    <TableCell>
                      <Typography noWrap sx={{ fontSize: '0.8125rem', color: theme.torq.text.primary }}>
                        {dayjs(row.emissao).format('DD/MM/YYYY')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={row.numero || 'N/A'}
                        size="small"
                        sx={{
                          height: '22px',
                          fontSize: '0.6875rem',
                          fontWeight: 600,
                          '& .MuiChip-label': { px: 1.25 },
                          bgcolor: theme.torq.surface.level2,
                          color: theme.torq.text.secondary,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography fontWeight={600} noWrap sx={{ fontSize: '0.8125rem', color: theme.torq.text.primary }}>
                        {row.nome || 'Cliente não informado'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography noWrap sx={{ fontSize: '0.8125rem', color: theme.torq.text.secondary }}>
                        {row.descricao || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography fontFamily="monospace" noWrap sx={{ fontSize: '0.8125rem', color: theme.torq.text.secondary }}>
                        {formatarDocumento(row.cpf || '')}
                      </Typography>
                    </TableCell>
                    <TableCell align='right'>
                      <Typography sx={{ fontSize: '0.8125rem', fontWeight: 700, color: 'success.main' }}>
                        {row.total.toLocaleString('pt-BR', {
                          style: 'currency',
                          currency: 'BRL'
                        })}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}

                {/* Linhas fantasma — sem elas, quando a página atual tem menos itens que
                    rowsPerPage (última página, ou poucos resultados), sobrava um espaço em
                    branco entre o último item real e o rodapé (o <tbody> não estica sozinho
                    pra preencher a altura do flex:1 do TableContainer). rowsPerPage já é
                    calculado pra caber exatamente no espaço disponível (ver useListagemAltura),
                    então completar até esse número faz a listagem sempre encostar no rodapé.
                    Mesmo <tbody> das linhas reais — nth-of-type precisa continuar a contagem
                    pro zebrado não "resetar" numa segunda tabela. */}
                {!isLoading && rows.length > 0 && rows.length < rowsPerPage && (
                  Array.from({ length: rowsPerPage - rows.length }).map((_, index) => (
                    <TableRow
                      key={`filler-${index}`}
                      sx={{
                        '&:last-of-type td': { borderBottom: 0 },
                        '& td': {
                          py: 1.25,
                          px: 1.5,
                          borderBottom: `1px solid ${theme.torq.border.default}`,
                        },
                        '&:nth-of-type(even)': {
                          backgroundColor: alpha(brand.menuBase, theme.palette.mode === 'dark' ? 0.08 : 0.04),
                        },
                      }}
                    >
                      <TableCell colSpan={6}>&nbsp;</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
              {rows.length === 0 && !isLoading && (
                <TableBody>
                  <TableRow>
                    <TableCell colSpan={6} align='center' sx={{ py: 3 }}>
                      <Box>
                        <ShoppingCartIcon sx={{
                          fontSize: 35,
                          color: theme.palette.mode === 'dark'
                            ? alpha(theme.palette.text.secondary, 0.4)
                            : 'text.secondary',
                          mb: 1
                        }} />
                        <Typography variant='h6' color='text.secondary' gutterBottom fontSize="0.85rem">
                          {Environment.LISTAGEM_VAZIA}
                        </Typography>
                        <Typography variant='body2' color='text.secondary' fontSize="0.7rem">
                          {filter || idparceiro ? 'Tente ajustar os filtros de busca' : 'Nenhuma venda encontrada no período'}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableBody>
              )}
            </Table>
          </TableContainer>

          {/* Rodapé/paginação — fora da <table>, num Box comum. Por estar fora da área
              rolável (TableContainer, flex:1 acima) e ser o último filho do Paper (flex
              column com altura sempre fixa), fica sempre colado na base da tela, mesmo
              quando a página atual tem poucas vendas (sem isso, sobrava uma área em branco
              entre o rodapé e o limite da tela — o antigo <TableFooter> ficava logo depois
              da última linha, não colado na base). */}
          <Box sx={{ flexShrink: 0, py: 1.25, px: 2, borderTop: `1px solid ${theme.torq.border.default}` }}>
            {isLoading && <LinearProgress sx={{ height: '1.5px', mb: 1.25 }} />}
            <Box display='flex' alignItems='center' justifyContent='space-between' flexWrap='wrap' gap={1.5}>
              <Typography variant='caption' color='text.secondary'>
                {totalCount === 0
                  ? 'Nenhuma venda encontrada'
                  : `Mostrando de ${primeiroItem} a ${ultimoItem} de ${totalCount} venda${totalCount !== 1 ? 's' : ''}`}
              </Typography>

              {totalCount > rowsPerPage && (
                <Pagination
                  page={pagina}
                  count={totalPaginas}
                  onChange={(_, newPage) => setSearchParams({
                    parceiro: idparceiro,
                    filter: filter,
                    tipo: tipo,
                    datainicial: dataInicial,
                    datafinal: dataFinal,
                    pagina: newPage.toString()
                  }, { replace: true })}
                  size="small"
                  shape="rounded"
                  showFirstButton
                  showLastButton
                  siblingCount={1}
                  boundaryCount={1}
                  sx={{
                    '& .MuiPaginationItem-root.Mui-selected': {
                      backgroundColor: brand.menuBase,
                      color: '#fff',
                      '&:hover': { backgroundColor: '#012D3D' },
                    },
                  }}
                />
              )}

              <FormControl size='small' sx={{ minWidth: 110 }}>
                <Select<number> value={rowsPerPage} onChange={handleChangeRowsPerPage}>
                  {(OPCOES_POR_PAGINA.includes(rowsPerPage) ? OPCOES_POR_PAGINA : [...OPCOES_POR_PAGINA, rowsPerPage].sort((a, b) => a - b)).map((opcao) => (
                    <MenuItem key={opcao} value={opcao}>{opcao} / página</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>
        </Paper>
      </LayoutBaseDePagina>
    </>
  );
};
